import React, { createContext, FC, useEffect, useMemo, useRef, useState, useContext } from "react";
import { useMutation } from "@tanstack/react-query";
import { cloneDeep, isEqual, Many, orderBy } from "lodash";
import { toast } from "react-toastify";
import {
  BenchmarkingCounty,
  BenchmarkingLineOfBusinessEnum,
  CompetitiveInsightsTabTypeEnum
} from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import BenchmarkingQHPService from "@app/overviews/network-benchmarking-dashboard/benchmarkingQHPService";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { BenchmarkingNetworkType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingNetworkType";
import { BenchmarkingParentOrganizationType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingParentOrganizationType";
import { CompetitiveInsightsSelectedSortType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/competitiveInsightsSelectedSortType";
import { useQHPPrimaryFilterOptionUpdates } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/hooks/useQHPPrimaryFilterOptionUpdates";
import { useQHPPrimaryFilters } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/hooks/useQHPPrimaryFilters";
import { MarketCoverageTypeEnum } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/MarketCoverageTypeEnum";
import { QHPNetwork } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPNetwork";
import { QHPNetworkTypeEnum } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPNetworkTypeEnum";
import { QHPParentOrganization } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPParentOrganization";
import { QHPParentOrganizationSortingTypeEnum } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPParentOrganizationSortingTypeEnum";
import { QHPPrimaryFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPPrimaryFiltersPayload";
import { QHPStarRatingTypeEnum } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPStarRatingTypeEnum";
import { UserContext } from "@app/user/UserContext";

export interface QHPFiltersContextValues {
  applyingServiceArea: QHPNetwork[];
  handleSetSelectedSortType: (sortType: CompetitiveInsightsSelectedSortType) => void;
  mappedStarRatings: DropdownItem<QHPStarRatingTypeEnum>[];
  marketCoverages: DropdownItem<MarketCoverageTypeEnum>[];
  networkFiltersLoading: boolean;
  onApplyPrimaryFiltersClick: () => void;
  onApplyServiceAreaClick: (countyIds: number[], networks: BenchmarkingNetworkType[]) => void;
  onClearNetworkFiltersClick: () => void;
  onClearPrimaryFiltersClick: () => void;
  parentOrganizations: QHPParentOrganization[];
  planTypes: DropdownItem<string>[];
  primaryFiltersLoading: boolean;
  selectedMarketCoverage: DropdownItem<MarketCoverageTypeEnum>[];
  selectedNetworkType: QHPNetworkTypeEnum;
  selectedParentOrganizations: QHPParentOrganization[];
  selectedPlanTypes: DropdownItem<string>[];
  selectedSortType: QHPParentOrganizationSortingTypeEnum;
  selectedStarRatings: DropdownItem<QHPStarRatingTypeEnum>[];
  setSelectedMarketCoverage: (selectedMarketCoverage: DropdownItem<MarketCoverageTypeEnum>[]) => void;
  setSelectedNetworkType: (selectedPlanOption: QHPNetworkTypeEnum) => void;
  setSelectedParentOrganizations: (selectedParentOrganizations: QHPParentOrganization[]) => void;
  setSelectedPlanTypes: (selectedPlanTypes: DropdownItem<string>[]) => void;
  setSelectedSortType: (selectedSortType: QHPParentOrganizationSortingTypeEnum) => void;
  setSelectedStarRatings: (selectedStarRatings: DropdownItem<QHPStarRatingTypeEnum>[]) => void;
  sortNetworks: (filteredParents: BenchmarkingParentOrganizationType[]) => BenchmarkingParentOrganizationType[];
}

const initialValues: QHPFiltersContextValues = {
  applyingServiceArea: [],
  handleSetSelectedSortType: () => null,
  mappedStarRatings: [],
  marketCoverages: [],
  networkFiltersLoading: false,
  onApplyPrimaryFiltersClick: () => null,
  onApplyServiceAreaClick: () => null,
  onClearNetworkFiltersClick: () => null,
  onClearPrimaryFiltersClick: () => null,
  parentOrganizations: [],
  planTypes: [],
  primaryFiltersLoading: false,
  selectedMarketCoverage: [],
  selectedNetworkType: QHPNetworkTypeEnum.All,
  selectedParentOrganizations: [],
  selectedPlanTypes: [],
  selectedSortType: null,
  selectedStarRatings: [],
  setSelectedMarketCoverage: () => null,
  setSelectedNetworkType: () => null,
  setSelectedParentOrganizations: () => null,
  setSelectedPlanTypes: () => null,
  setSelectedSortType: () => null,
  setSelectedStarRatings: () => null,
  sortNetworks: () => null,
}

export const QHPFiltersContext = createContext(initialValues);

interface ApplyPrimaryFiltersVariables {
  applyingServiceArea: QHPNetwork[];
  primaryFilters: QHPPrimaryFiltersPayload;
}

const QHPFiltersContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const [applyingServiceArea, setApplyingServiceArea] = useState<QHPNetwork[]>(initialValues.applyingServiceArea);
  const [planTypes, setPlanTypes] = useState<DropdownItem<string>[]>(initialValues.planTypes);
  const [mappedStarRatings, setMappedStarRatings] = useState<DropdownItem<QHPStarRatingTypeEnum>[]>(initialValues.mappedStarRatings);
  const [parentOrganizations, setParentOrganizations] = useState<QHPParentOrganization[]>(initialValues.parentOrganizations);
  const [selectedParentOrganizations, setSelectedParentOrganizations] = useState<QHPParentOrganization[]>([]);
  const [selectedSortType, setSelectedSortType] = useState<QHPParentOrganizationSortingTypeEnum>(null);
  const [selectedStarRatings, setSelectedStarRatings] = useState<DropdownItem<QHPStarRatingTypeEnum>[]>(initialValues.selectedStarRatings);
  const [selectedMarketCoverage, setSelectedMarketCoverage] = useState<DropdownItem<MarketCoverageTypeEnum>[]>(initialValues.selectedMarketCoverage);
  const [selectedNetworkType, setSelectedNetworkType] = useState<QHPNetworkTypeEnum>(initialValues.selectedNetworkType);
  const [selectedPlanTypes, setSelectedPlanTypes] = useState<DropdownItem<string>[]>(initialValues.selectedPlanTypes);
  
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const { clientId } = useContext(UserContext);
  const benchmarkingQHPService = useMemo(() => new BenchmarkingQHPService(), []);
  const { isPending: networkFiltersLoading, mutate: applyPrimaryFiltersMutation } = useMutation({
    mutationFn: ({ primaryFilters }: ApplyPrimaryFiltersVariables) => benchmarkingQHPService.getBenchmarkingNetworkData(primaryFilters),
    onSuccess: (data, variables) => {
      if (!data) return;

      const yourNetworks: QHPParentOrganization = {
        hContracts: data.yourNetworks,
        issuerIds: [data.yourNetworks[0]?.issuerId],
        parentOrganizationName: "Your QES Networks",
      };

      for (const parentOrg of data.comparisonNetworks) {
        for (const contract of parentOrg.hContracts) {
          contract.parentOrganization = parentOrg.parentOrganizationName;
        }
      }

      let newParentOrganizations = [yourNetworks];
      newParentOrganizations = newParentOrganizations.concat(data.comparisonNetworks);

      for (const parentOrg of newParentOrganizations) {
        for (const contract of parentOrg.hContracts) {
          contract.parentOrganization = parentOrg.parentOrganizationName;
        }
      }

      setParentOrganizations(newParentOrganizations);

      const newSelectedParentOrganizations: QHPParentOrganization[] = [cloneDeep(yourNetworks)];
      const newSelectedNetworks: QHPNetwork[] = [];

      const handlePreviousSelections = (serviceArea: BenchmarkingNetworkType): void => {
        const parentOrganization: QHPParentOrganization = newParentOrganizations.find(x => x.parentOrganizationName === serviceArea.parentOrganization);

        if (!!parentOrganization) {
          const newContract: QHPNetwork = parentOrganization.hContracts.find(x => x.contractId === serviceArea.contractId);

          // the previously-selected contract must be re-acquired in order to obtain the latest map data
          if (!!newContract) {
            newSelectedNetworks.push(newContract);
          }

          // if the parent organization of the previously-selected contract is not your qes networks (which is already selected by default),
          // and if it is not already selected, select it
          if (parentOrganization.parentOrganizationName !== yourNetworks.parentOrganizationName && !newSelectedParentOrganizations.includes(parentOrganization)) {
            newSelectedParentOrganizations.push(parentOrganization);
          }
        }
      }

      if (!!variables.applyingServiceArea.length) {
        // when applying service area
        toast("Service Area Updated", {
          containerId: "z-index-9999",
          type: "success",
        });

        for (const serviceArea of variables.applyingServiceArea) {
          if (!serviceArea.disabled) {
            handlePreviousSelections(serviceArea);
          }
        }

        if (benchmarkingFiltersContext.selectedTab !== CompetitiveInsightsTabTypeEnum.NetworkSelection) {
          benchmarkingFiltersContext.setSelectedTab(CompetitiveInsightsTabTypeEnum.NetworkSelection)
        }

        setApplyingServiceArea([]);
      } else {
        // when retaining networks from a previous search
        for (const previousSelectedNetwork of benchmarkingFiltersContext.previousSelectedNetworks) {
          if (newSelectedNetworks.length < 5) {
            handlePreviousSelections(previousSelectedNetwork);
          }
        }
      }

      benchmarkingFiltersContext.setSelectedNetworks(newSelectedNetworks);
      setSelectedParentOrganizations(newSelectedParentOrganizations);
    },
  });

  const starRatings: DropdownItem<QHPStarRatingTypeEnum>[] = Object.values(QHPStarRatingTypeEnum).map(x => {
    return { key: x, name: x }
  });

  const isMarketplace = benchmarkingFiltersContext.selectedLineOfBusiness === BenchmarkingLineOfBusinessEnum.Marketplace;

  // guards the one-time plan-type population below against a background refetch (e.g. window
  // refocus after staleTime elapses) silently resetting the user's in-progress plan-type selection
  const planTypesInitializedRef = useRef(false);

  const { isLoading: primaryFiltersLoading, primaryFilterData } = useQHPPrimaryFilters(clientId, isMarketplace);

  const marketCoverages: DropdownItem<MarketCoverageTypeEnum>[] = Object.values(MarketCoverageTypeEnum).map(x => {
    return { key: x, name: x }
  });

  useEffect(() => {
    if (!isMarketplace) return;

    planTypesInitializedRef.current = false;
    benchmarkingFiltersContext.setDisplayedPrimaryFilters(null);
    benchmarkingFiltersContext.setSelectedState(null);
    benchmarkingFiltersContext.setCounties([]);
    benchmarkingFiltersContext.setRawCounties([]);
    benchmarkingFiltersContext.setSelectedCounties([]);
    benchmarkingFiltersContext.setCountyIdFilter(null);
    setMappedStarRatings(cloneDeep(starRatings));
    setSelectedStarRatings(cloneDeep(starRatings));
    setSelectedMarketCoverage(cloneDeep(marketCoverages));
    setSelectedNetworkType(QHPNetworkTypeEnum.All);
  }, [isMarketplace]);

  useEffect(() => {
    if (!primaryFilterData || !isMarketplace) return;

    benchmarkingFiltersContext.processSpecialties(primaryFilterData.specialties);
    benchmarkingFiltersContext.setStates(primaryFilterData.states);
    benchmarkingFiltersContext.setAffiliationAuthorizedStates(primaryFilterData.affiliationAuthorizedStates);
    benchmarkingFiltersContext.setMarketProjectId(primaryFilterData.marketProjectId);

    if (!planTypesInitializedRef.current) {
      const newPlanTypes = primaryFilterData.planTypes.map(x => { return { key: x, name: x } });
      setPlanTypes(cloneDeep(newPlanTypes));
      setSelectedPlanTypes(cloneDeep(newPlanTypes));
      planTypesInitializedRef.current = true;
    }
  }, [primaryFilterData, isMarketplace]);

  useEffect(() => {
    if (!!selectedSortType) {
      setSelectedSortType(null);
    }
  }, [networkFiltersLoading]);

  useEffect(() => {
    if (!!parentOrganizations?.length && !selectedSortType) {
      setSelectedSortType(QHPParentOrganizationSortingTypeEnum.AlphabeticalAsc);
    }
  }, [parentOrganizations]);

  useEffect(() => {
    if (!!applyingServiceArea.length) {
      onApplyPrimaryFiltersClick();
    } else {
      const countiesEqual = isEqual(
        benchmarkingFiltersContext.displayedPrimaryFilters?.countyIds?.map(x => x.toString()).sort((a, b) => a.localeCompare(b)),
        benchmarkingFiltersContext.selectedCounties.sort((a, b) => a.localeCompare(b))
      );

      const starRatingsEqual = isEqual(
        benchmarkingFiltersContext.displayedPrimaryFilters?.starRatings?.sort(),
        selectedStarRatings.map(x => x.key).sort()
      );

      const planOptionEqual = isEqual(
        (benchmarkingFiltersContext.displayedPrimaryFilters as QHPPrimaryFiltersPayload)?.networkType,
        selectedNetworkType
      );

      const planTypesEqual = isEqual(
        benchmarkingFiltersContext.displayedPrimaryFilters?.planTypes?.sort(),
        selectedPlanTypes.map(x => x.key).sort()
      );

      const marketCoverageEqual = isEqual(
        (benchmarkingFiltersContext.displayedPrimaryFilters as QHPPrimaryFiltersPayload)?.marketCoverage?.sort(),
        selectedMarketCoverage.map(x => x.key).sort()
      );

      if ((!countiesEqual || !starRatingsEqual || !planOptionEqual || !planTypesEqual || !marketCoverageEqual) && !!benchmarkingFiltersContext.selectedNetworks.length) {
        benchmarkingFiltersContext.setPreviousSelectedNetworks(cloneDeep(benchmarkingFiltersContext.selectedNetworks));
      }

      if (!!benchmarkingFiltersContext.displayedPrimaryFilters || !!benchmarkingFiltersContext.displayedNetworkFilters?.length) {
        benchmarkingFiltersContext.setDisplayedPrimaryFilters(null);
        onClearNetworkFiltersClick();
      }
    }
  }, [
    benchmarkingFiltersContext.selectedState,
    benchmarkingFiltersContext.selectedCounties,
    selectedStarRatings,
    selectedNetworkType,
    selectedPlanTypes,
    selectedMarketCoverage,
  ]);

  useEffect(() => {
    if (!!benchmarkingFiltersContext.displayedNetworkFilters?.length) {
      // on network change, clear all displayed network filters
      benchmarkingFiltersContext.setDisplayedNetworkFilters([]);
    }
  }, [benchmarkingFiltersContext.selectedNetworks, selectedParentOrganizations]);

  const { primaryFilterUpdates } = useQHPPrimaryFilterOptionUpdates(clientId, benchmarkingFiltersContext.selectedState?.stateId, isMarketplace);

  useEffect(() => {
    if (!primaryFilterUpdates) return;

    benchmarkingFiltersContext.setMarketProjectId(primaryFilterUpdates.marketProjectId);
    benchmarkingFiltersContext.processSpecialties(primaryFilterUpdates.specialtyGroups);
  }, [primaryFilterUpdates]);

  const handleSetSelectedSortType = (sortType: CompetitiveInsightsSelectedSortType) => {
    setSelectedSortType(sortType as QHPParentOrganizationSortingTypeEnum);
  }

  const onApplyServiceAreaClick = (countyIds: number[], networks: BenchmarkingNetworkType[]): void => {
    const newSelectedCounties: BenchmarkingCounty[] = benchmarkingFiltersContext.rawCounties.filter(x => countyIds.includes(x.countyId));
    benchmarkingFiltersContext.setSelectedCounties(newSelectedCounties.map(x => x.countyId.toString()))
    setApplyingServiceArea(networks as QHPNetwork[]);
  }

  const onApplyPrimaryFiltersClick = (): void => {
    const formatPayload = (payload: QHPPrimaryFiltersPayload) => {
      if (payload.starRatings.length === mappedStarRatings.length) {
        payload.starRatings = [];
      }
      if (payload.marketCoverage.length === marketCoverages.length) {
        payload.marketCoverage = [];
      }
      if (payload.planTypes.length === planTypes.length) {
        payload.planTypes = [];
      }
    }

    const primaryFilters: QHPPrimaryFiltersPayload = {
      countyIds: benchmarkingFiltersContext.selectedCounties.map(x => parseInt(x)),
      lineOfBusinessId: benchmarkingFiltersContext.selectedLineOfBusiness,
      marketCoverage: selectedMarketCoverage.map(x => x.key),
      networkType: selectedNetworkType,
      planTypes: selectedPlanTypes.map(x => x.key),
      starRatings: selectedStarRatings.map(x => x.key),
      stateAbbreviation: benchmarkingFiltersContext.selectedState.stateAbbreviation
    };

    benchmarkingFiltersContext.setDisplayedPrimaryFilters(cloneDeep(primaryFilters));

    formatPayload(primaryFilters);

    applyPrimaryFiltersMutation({ applyingServiceArea, primaryFilters });
  }

  const onClearPrimaryFiltersClick = (): void => {
    benchmarkingFiltersContext.setSelectedCounties([]);
    benchmarkingFiltersContext.setCounties([]);
    benchmarkingFiltersContext.setRawCounties([]);
    benchmarkingFiltersContext.setSelectedState(null);
    benchmarkingFiltersContext.setDisplayedPrimaryFilters(null);
    setSelectedStarRatings(cloneDeep(starRatings));
    setSelectedMarketCoverage(cloneDeep(marketCoverages));
    setSelectedNetworkType(QHPNetworkTypeEnum.All);
    setSelectedPlanTypes(cloneDeep(planTypes));
    // if resetting the primary filters,
    // also reset the network filters that are based off of the primary filters
    onClearNetworkFiltersClick();
  }

  const onClearNetworkFiltersClick = (): void => {
    setSelectedParentOrganizations([]);
    benchmarkingFiltersContext.setSelectedNetworks([]);
    benchmarkingFiltersContext.setDisplayedNetworkFilters([]);
  }

  const sortByNetworkCount = (qesNetwork: QHPParentOrganization, benchmarkingParentOrganizations: QHPParentOrganization[], sortDirection: Many<boolean | "asc" | "desc">): QHPParentOrganization[] => {
    benchmarkingParentOrganizations = orderBy(benchmarkingParentOrganizations, (p: QHPParentOrganization) => p.hContracts.length, sortDirection);
    benchmarkingParentOrganizations.forEach((p) => {
      const sortedHContracts = orderBy(p.hContracts, (h: QHPNetwork) => h.contractId.length, sortDirection);
      p.hContracts = sortedHContracts;
    });

    return benchmarkingParentOrganizations;
  }

  const sortByName = (qesNetwork: QHPParentOrganization, benchmarkingParentOrganizations: QHPParentOrganization[], sortDirection: Many<boolean | "asc" | "desc">): QHPParentOrganization[] => {
    benchmarkingParentOrganizations = orderBy(benchmarkingParentOrganizations, (p) => p.parentOrganizationName, sortDirection);
    benchmarkingParentOrganizations.forEach((p) => {
      const sortedNetworks = orderBy(p.hContracts, (h) => h.contractId, sortDirection);
      p.hContracts = sortedNetworks;
    });

    if (!!qesNetwork) {
      qesNetwork.hContracts = orderBy(qesNetwork.hContracts, (h) => h.contractId, sortDirection);
    }

    return benchmarkingParentOrganizations;
  }

  const sortNetworks = (filteredParents: BenchmarkingParentOrganizationType[]): BenchmarkingParentOrganizationType[] => {
    if (!!selectedSortType) {
      // "your qes networks" pill should be excluded from sorting and should always maintain the first position
      const qhpParentOrganizations: QHPParentOrganization[] = filteredParents as QHPParentOrganization[];
      const yourQesNetworks = cloneDeep(qhpParentOrganizations.find(x => x.parentOrganizationName === "Your QES Networks"));
      let newSortedBenchmarkingParentOrganizations = cloneDeep(qhpParentOrganizations.filter(x => x.parentOrganizationName !== "Your QES Networks"));

      switch (selectedSortType) {
        case QHPParentOrganizationSortingTypeEnum.AlphabeticalAsc:
          newSortedBenchmarkingParentOrganizations = sortByName(yourQesNetworks, newSortedBenchmarkingParentOrganizations, "asc");
          break;
        case QHPParentOrganizationSortingTypeEnum.AlphabeticalDesc:
          newSortedBenchmarkingParentOrganizations = sortByName(yourQesNetworks, newSortedBenchmarkingParentOrganizations, "desc");
          break;
        case QHPParentOrganizationSortingTypeEnum.NetworkCountAsc:
          newSortedBenchmarkingParentOrganizations = sortByNetworkCount(yourQesNetworks, newSortedBenchmarkingParentOrganizations, "asc");
          break;
        case QHPParentOrganizationSortingTypeEnum.NetworkCountDesc:
          newSortedBenchmarkingParentOrganizations = sortByNetworkCount(yourQesNetworks, newSortedBenchmarkingParentOrganizations, "desc");
          break;
        default:
          break;
      }

      filteredParents = !!yourQesNetworks ? [yourQesNetworks] : [];
      filteredParents = filteredParents.concat(newSortedBenchmarkingParentOrganizations);

      // update the selectedParentOrganizations with the sorted items so the UI gets updated
      selectedParentOrganizations.forEach((p, index: number) => {
        const selectedParentIndex = filteredParents.findIndex(o => o.parentOrganizationName === p.parentOrganizationName);
        if (selectedParentIndex > -1) {
          selectedParentOrganizations.splice(index, 1, (filteredParents as QHPParentOrganization[])[selectedParentIndex]);
        }
      });
    }

    return filteredParents;
  }

  return (
    <QHPFiltersContext.Provider
      value={{
        applyingServiceArea,
        handleSetSelectedSortType,
        mappedStarRatings,
        marketCoverages,
        networkFiltersLoading,
        onApplyPrimaryFiltersClick,
        onApplyServiceAreaClick,
        onClearNetworkFiltersClick,
        onClearPrimaryFiltersClick,
        parentOrganizations,
        planTypes,
        primaryFiltersLoading,
        selectedMarketCoverage,
        selectedNetworkType,
        selectedParentOrganizations,
        selectedPlanTypes,
        selectedSortType,
        selectedStarRatings,
        setSelectedMarketCoverage,
        setSelectedNetworkType,
        setSelectedParentOrganizations,
        setSelectedPlanTypes,
        setSelectedSortType,
        setSelectedStarRatings,
        sortNetworks,
      }}>
      {props.children}
    </QHPFiltersContext.Provider>
  )
}

export default QHPFiltersContextProvider;